import React, { useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';
import TabCard from '../TabCard';
import ChatHomeButton from '../ChatHomeButton';
import ChatHomeLink from '../ChatHomeLink';
import TabTitle from '../TabTitle';
import SlotWrapper from '../SlotWrapper/SlotWrapper';
import TabSlider from '../TabSlider';
import ChatIcon from '../Icons/ChatIcon';
import ConversationChatIcon from '../Icons/ConversationChatIcon';
import * as tasksService from '@/services/tabs.service';
import { useConversationStore } from '@/hooks/useConversationStore'
import { usePreviousConversationsStore } from '@/stores/previousConversation/previousConversationStore';
import { getIconComponent } from '@/v3/helpers/iconMapping';
import DLoaderBubbleLoading from '@/components/Global/DLoaderBubbleLoading';
import FullDanteLogoGreyIcon from '../Icons/FullDanteLogoGreyIcon';


const HomeTabContent = ({
  config,
  loading = false,
  // error = null,
  className = '',
  onNavigate,
  kb_id,
  token,
  slots, // Add slots prop
  previous_conversation_enabled = config?.previous_conversation_enabled ?? true,
  interfaceType = 'bubble', // Add interfaceType prop with default value
  isInIframe = false, // Add isInIframe prop with default value
  isPreviewMode = false, // Add isPreviewMode prop to show button in preview
  ...props
}) => {

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const [hasShownInitialAnimation, setHasShownInitialAnimation] = useState(false);
  const [apiCallCompleted, setApiCallCompleted] = useState(false);

  // Use the conversation store with automatic namespacing
  const {
    resetCurrentConversation,
  } = useConversationStore(kb_id, interfaceType);

  // Get previous conversations to check if there are any
  const { conversations, fetchConversations } = usePreviousConversationsStore();

  // Filter conversations by kb_id and interfaceType
  const conversationsForThisInterface = conversations[kb_id]?.filter(
    conv => conv.interfaceType === interfaceType
  ) || [];
  const hasLastConversation = conversationsForThisInterface.length > 0;



  const getSlots = async () => {
    setIsLoading(true);
    setApiCallCompleted(false);

    try {
      const response = await tasksService.getAllSlotsV2(kb_id, token);
      if (response.status === 200) {
        setData(response.data);
        setIsDataReady(true);
      }
    } catch (e) {
      // Even on error, we should show something
      setIsDataReady(true);
    } finally {
      setIsLoading(false);
      setApiCallCompleted(true);
    }
  };


  // Fetch previous conversations on mount
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    const updateData = async () => {
      // If animation has already been shown, load content immediately without animation
      if (hasShownInitialAnimation) {
        if (slots) {
          setData(slots);
          setIsDataReady(true);
          setApiCallCompleted(true);
        } else if (kb_id) {
          await getSlots();
        } else {
          setIsDataReady(true);
          setApiCallCompleted(true);
        }
        return;
      }

      // First time loading - show animation
      setHasShownInitialAnimation(true);

      // If slots are provided (e.g., in preview mode), use them instead of fetching
      if (slots) {
        setData(slots);
        setIsDataReady(true);
        setApiCallCompleted(true);
      } else if (kb_id) {
        await getSlots();
      } else {
        // No data to load
        setIsDataReady(true);
        setApiCallCompleted(true);
      }
    };

    updateData();
  }, [kb_id, slots, hasShownInitialAnimation]);

  // // Loading state
  // if (loading) {
  //   return (
  //     <div className={`flex-1 flex items-center justify-center p-v3-md ${className} `}>
  //       <div className="text-center">
  //         <div className="animate-spin rounded-full h-v3-lg w-v3-lg border-b-2 border-v3-purple-primary mx-auto mb-v3-sm"></div>
  //         <p className="text-v3-black-secondary text-v3-h6">Loading content...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // Error state
  // if (error) {
  //   return (
  //     <div className={`flex-1 flex items-center justify-center p-v3-md ${className}`}>
  //       <div className="text-center">
  //         <p className="text-v3-red-primary text-v3-h6 mb-v3-xs">Error loading content</p>
  //         <p className="text-v3-black-secondary text-sm">{error}</p>
  //       </div>
  //     </div>
  //   );
  // }

  const renderSlotButton = (button, index) => {
    if (!button) return null;

    const { label, icon: iconValue, link, button_type = 'primary' } = button;
    
    // Convert icon string value to icon component
    const Icon = getIconComponent(iconValue);

    if (button_type === 'primary') {
      return (
        <SlotWrapper key={`button-${index}`}>
          <ChatHomeButton
            label={label}
            icon={Icon}
            config={config}
            onClick={() => {
              if (link) {
                window.open(link, '_blank');
              }
            }}
          />
        </SlotWrapper>
      );
    }

    if (button_type === 'secondary') {
      return (
        <SlotWrapper key={`button-${index}`}>
          <ChatHomeLink
            label={label}
            icon={Icon}
            href={link}
            config={config}
          />
        </SlotWrapper>
      );
    }

    return null;
  };

  const renderSlotTitle = (title, index) => {
    if (!title) return null;

    const { content } = title;

    return (
      <SlotWrapper key={`title-${index}`}>
        {content && <TabTitle title={content} config={config} />}
      </SlotWrapper>
    );
  };

  const renderSlotCard = (cardItem, cardType, index) => {
    if (!cardItem) return null;

    // Extract the actual card data from the item structure (similar to how sliders work)
    const cardData = cardItem.item || cardItem.card || cardItem;

    // Map the data to the correct prop names for TabCard
    const getTabCardProps = () => {
      switch (cardType) {
        case 'image':
          return {
            type: 'picture',
            image: cardData.image_url,
            text: cardData.title || cardData.image_title,
            description: cardData.description || cardData.image_description,
            link: cardData.url
          };
        case 'meta_link':
          return {
            type: 'picture',
            image: cardData.meta_image_url,
            text: cardData.title || cardData.meta_title,
            description: cardData.description || cardData.meta_description,
            link: cardData.url // Pass URL as link, fallback to meta_image_url
          };
        case 'video':
          return {
            type: 'video',
            video: cardData.video_url || cardData.image_url, // For video cards, the video URL might be stored in image_url field
            text: cardData.title || cardData.video_title,
            description: cardData.description || cardData.video_description,
            link: cardData.url || cardData.video_url 
          };
        default:
          return {};
      }
    };


    return (
      <SlotWrapper key={`card-${cardType}-${index}`}>
        <TabCard {...getTabCardProps()} config={config} />
      </SlotWrapper>
    );
  };

  const renderSlotSliders = (slider, index) => {
    // Handle proper slider structure (slider with items array)
    if (!slider || !slider.items || !Array.isArray(slider.items)) return null;

    const slides = sortByOrder(slider.items).map((item) => {
      const {
        slide_url,
        slide_image_url,
        slide_description,
        slide_title,
        order
      } = item;

      return {
        type: slider.type || 'landscape', // Use slider type from the slider object
        image: slide_image_url,
        title: slide_title || '', // Include slide title
        description: slide_description,
        link: slide_url,
        order
      };
    });

    return (
      <SlotWrapper key={`slider-${index}`}>
        <TabSlider slides={slides} type={slider.type} config={config} />
      </SlotWrapper>
    );
  };

  const renderIntroMessage = (introMessage) => {
    if (!introMessage) return null;

    // Convert line breaks to HTML <br> tags
    const formattedMessage = introMessage.replace(/\n/g, '<br>');

    return (
      <div className={`flex flex-col items-start p-v3-lg ${isInIframe ? 'mt-v3-lg' : ''}`}>
        <p
          className='text-v3-h4 font-bolder max-w-[365px] break-words'
          style={{
            color: 'var(--dt-color-element-100)',
            fontFamily: config?.font_name ? `${config.font_name}, inter-variable` : undefined,
            lineHeight: '32px',
            letterSpacing: '-0.5px',
          }}
          dangerouslySetInnerHTML={{ __html: formattedMessage }}
        />
      </div>
    );
  };



  // Helper function to sort items by order
  const sortByOrder = (items) => {
    if (!Array.isArray(items)) return items;
    return items.sort((a, b) => (a.order || 0) - (b.order || 0));
  };

  // Get all slots and sort them by order
  const getAllSlots = () => {
    const slots = [];

    // Handle titles
    if (data?.titles && Array.isArray(data.titles)) {
      data.titles.forEach((title, index) => {
        slots.push({
          type: 'title',
          data: title,
          order: title.order || 0,
          id: `title-${index}`,
          index
        });
      });
    }
    
    // Handle buttons
    if (data?.buttons && Array.isArray(data.buttons)) {
      data.buttons.forEach((button, index) => {
        slots.push({
          type: 'button',
          data: button,
          order: button.order || 0,
          id: `button-${index}`,
          index
        });
      });
    }
    
    // Handle image cards
    if (data?.image_cards && Array.isArray(data.image_cards)) {
      data.image_cards.forEach((card, index) => {
        slots.push({
          type: 'card',
          data: card,
          cardType: 'image',
          order: card.order || 0,
          id: `image-card-${index}`,
          index
        });
      });
    }

    // Handle meta link cards
    if (data?.meta_link_cards && Array.isArray(data.meta_link_cards)) {
      data.meta_link_cards.forEach((card, index) => {
        slots.push({
          type: 'card',
          data: card,
          cardType: 'meta_link',
          order: card.order || 0,
          id: `meta-link-card-${index}`,
          index
        });
      });
    }

    // Handle video cards
    if (data?.video_cards && Array.isArray(data.video_cards)) {
      data.video_cards.forEach((card, index) => {
        slots.push({
          type: 'card',
          data: card,
          cardType: 'video',
          order: card.order || 0,
          id: `video-card-${index}`,
          index
        });
      });
    }

    // Handle new cards structure (for backward compatibility and preview mode)
    if (data?.cards && Array.isArray(data.cards)) {
      data.cards.forEach((cardItem, index) => {
        slots.push({
          type: 'card',
          data: cardItem,
          cardType: cardItem.card_type,
          order: cardItem.order || 0,
          id: cardItem.id || `card-${index}`,
          index
        });
      });
    }

    // Handle sliders
    if (data?.sliders && Array.isArray(data.sliders)) {
      data.sliders.forEach((slider, index) => {
        slots.push({
          type: 'sliders',
          data: slider,
          order: slider.order || 0,
          id: `slider-${index}`,
          index
        });
      });
    }

    return slots.sort((a, b) => a.order - b.order);
  };

  const renderSlot = (slot) => {
    switch (slot.type) {
      case 'title':
        return renderSlotTitle(slot.data, slot.index);
      case 'card':
        return renderSlotCard(slot.data, slot.cardType, slot.index);
      case 'button':
        return renderSlotButton(slot.data, slot.index);
      case 'sliders':
        return renderSlotSliders(slot.data, slot.index);
      default:
        return null;
    }
  };

  return (
    <>
      <Transition
        show={isLoading || (hasShownInitialAnimation && !isDataReady)}
        // show={true}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute inset-0 flex items-center justify-center z-[9999] bg-transparent">
          <DLoaderBubbleLoading show={true} height="h-auto" />
        </div>
      </Transition>

      <div className={`bubble-home-content flex-1 h-full ${className} relative ${!config?.intro_message_enabled || !config?.intro_message ? 'pt-v3-huge' : ''}`} {...props}>

      {/* Content - show when data is ready and API call completed */}
      <Transition
            show={isDataReady && apiCallCompleted}
            enter="transition-opacity duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="h-full">
              <div className="flex-1">
                {config?.intro_message_enabled && config?.intro_message && renderIntroMessage(config?.intro_message)}
                <div className="px-v3-sm">
                  <SlotWrapper key="ask-question-button">
                    <ChatHomeButton
                      label={config?.ask_a_question_button || "Ask a question"}
                      icon={ChatIcon}
                      config={config}
                      className='ask-question-button'
                      onClick={() => {
                        // Reset to start a fresh new conversation
                        resetCurrentConversation();
                        // Set a small delay to ensure reset completes before navigating
                        setTimeout(() => {
                          if (onNavigate) {
                            onNavigate('chat');
                          }
                        }, 50);
                      }}
                    />
                  </SlotWrapper>
                  {previous_conversation_enabled && (hasLastConversation || isPreviewMode) && (
                    <SlotWrapper key="continue-conversation-button">
                      <ChatHomeButton
                        label={config?.continue_last_conversation_button || "Continue last conversation"}
                        icon={ConversationChatIcon}
                        config={config}
                        className='continue-conversation-button'
                        onClick={() => {
                          if (onNavigate) {
                            onNavigate('chat-previous-conversations');
                          }
                        }}
                        // numberBadge={1}
                      />
                    </SlotWrapper>
                  )}
                </div>
                <div className="pb-v3-lg">
                  {getAllSlots().map((slot) => (
                    <div key={slot.id} className="px-v3-sm">
                      {renderSlot(slot)}
                    </div>
                  ))}
                </div>
              </div>
              {interfaceType === 'direct-link' && !config?.remove_watermark && (
                <div className='px-v3-sm w-full'>
                  <div className='flex items-center justify-center w-full py-[12px]'>
                    <div className="flex items-center gap-1 cursor-pointer" onClick={() => {
                      window.open(`https://dante-ai.com/?utm_source=cht-${kb_id}&utm_medium=pwdByDante&utm_campaign=poweredByDante`, '_blank');
                    }}>
                      <p className="text-xs font-medium text-v3-black-secondary leading-4">Powered by </p>
                      <FullDanteLogoGreyIcon className="w-[56px] h-[14px]" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Transition>
    </div>
    </>
  );
};

export default HomeTabContent;
